import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  Fade,
  Backdrop,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Error } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../components/context/AuthContext';

// Custom MUI Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#115293',
      contrastText: '#ffffff',
    },
    background: {
      default: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          padding: '10px 16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            transition: 'all 0.2s ease',
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#115293',
              boxShadow: '0 0 8px rgba(25, 118, 210, 0.3)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        },
      },
    },
  },
});

const LoginPage = React.memo(() => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { login, error, setError } = useAuth();
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === 'email') {
        setEmailError(validateEmail(value) || value === '' ? '' : 'Invalid email format');
      }
      if (error) setError(null);
    },
    [error, setError]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (emailError) return;
      setLoading(true);

      const result = await login(formData.email, formData.password);

      if (result.success) {
        navigate('/email-sender');
      }

      setLoading(false);
    },
    [formData, login, navigate, emailError]
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.background.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container maxWidth="xs">
          <Fade in timeout={800}>
            <Paper elevation={12} sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontSize: { xs: '1.8rem', sm: '2rem', md: '2.25rem' } }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Sign in to your account
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Fade in timeout={300}>
                  <Alert
                    severity="error"
                    icon={<Error />}
                    sx={{ mb: 3, borderRadius: '8px' }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      size="medium"
                      autoComplete="email"
                      error={!!emailError}
                      helperText={emailError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ 'aria-label': 'Email address' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      size="medium"
                      autoComplete="current-password"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ 'aria-label': 'Password' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || !!emailError}
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Don’t have an account?{' '}
                  <MuiLink
                    component={RouterLink}
                    to="/register"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Sign up
                  </MuiLink>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Forgot password?{' '}
                  <MuiLink
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Reset here
                  </MuiLink>
                </Typography>
                <MuiLink
                  component={RouterLink}
                  to="/"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  ← Back to Home
                </MuiLink>
              </Box>
            </Paper>
          </Fade>
        </Container>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </ThemeProvider>
  );
});

export default LoginPage;